import React, { FC } from "react";

interface Application {
  _id: string;
  programId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  education: string;
  experience: string;
  motivation: string;
  expectations: string;
  heardFrom: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

interface ApplicationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
}

const ApplicationDetailsModal: FC<ApplicationDetailsModalProps> = ({
  isOpen,
  onClose,
  application,
}) => {
  if (!isOpen || !application) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Application Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.firstName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <p className="mt-1 text-sm text-gray-900">{application.lastName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{application.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{application.phone}</p>
              </div>
            </div>
          </div>

          {/* Education */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Education</h3>
            <p className="text-sm text-gray-900">{application.education}</p>
          </div>

          {/* Experience */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Experience</h3>
            <p className="text-sm text-gray-900">{application.experience}</p>
          </div>

          {/* Motivation */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Motivation</h3>
            <p className="text-sm text-gray-900">{application.motivation}</p>
          </div>

          {/* Expectations */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Expectations</h3>
            <p className="text-sm text-gray-900">{application.expectations}</p>
          </div>

          {/* How they heard about us */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">How did you hear about us?</h3>
            <p className="text-sm text-gray-900">{application.heardFrom}</p>
          </div>

          {/* Application Info */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Application Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                  application.status === "approved" ? "bg-green-100 text-green-800" :
                  application.status === "rejected" ? "bg-red-100 text-red-800" :
                  "bg-yellow-100 text-yellow-800"
                }`}>
                  {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Applied On</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(application.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsModal;
